import React, { useEffect, useState } from 'react'
import { Box, Chip, Typography, Popover } from '@mui/material'
import { StepProps } from '../../../components/_WMS/Stepper'
import { AsnReceptionResult, AsnReceptionState, eStepAsnReception } from '../types'
import { ExpandableCardList, ExpandableCardField } from '../../../components/_WMS/ExpandableCardList/ExpandableCardList'
import { formatExpirationDate } from '../../../utils/dateHelper'

interface ResultsStepProps extends StepProps<AsnReceptionState> {}

const MAX_VISIBLE = 3

const LpnChips: React.FC<{ lpns: string[] }> = ({ lpns }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const visible = lpns.slice(0, MAX_VISIBLE)
  const hidden = lpns.slice(MAX_VISIBLE)

  if (!lpns.length) return <Typography variant='body2'>Sin LPN</Typography>

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
      {visible.map((lpn) => (
        <Chip key={lpn} label={lpn} size='small' color='primary' variant='outlined'
          sx={{ fontWeight: 600, fontSize: '0.72rem', borderRadius: '20px' }} />
      ))}
      {hidden.length > 0 && (
        <>
          <Chip
            label={`+${hidden.length}`}
            size='small'
            onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget) }}
            sx={{ fontWeight: 700, fontSize: '0.72rem', borderRadius: '20px', cursor: 'pointer',
              backgroundColor: '#e3f0ff', color: '#004bb1', border: '1px solid #004bb1' }}
          />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflowY: 'auto' }}>
              <Typography variant='caption' sx={{ fontWeight: 700, color: '#555', mb: 0.5 }}>Todas las LPNs</Typography>
              {lpns.map((lpn) => (
                <Chip key={lpn} label={lpn} size='small' color='primary' variant='outlined'
                  sx={{ fontWeight: 600, fontSize: '0.72rem', borderRadius: '20px' }} />
              ))}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  )
}

const asnFields: ExpandableCardField<AsnReceptionResult>[] = [
  { label: 'Proveedor', getValue: (item) => item.supplierName },
  { label: 'Origen', getValue: (item) => item.supplierOriginName },
  { label: 'Unidades', getValue: (item) => item.unitsQty },
  { label: 'Marca', getValue: (item) => item.brandName },
  { label: 'Creada', getValue: (item) => item.createdDate ? formatExpirationDate(item.createdDate, 'dd-MM-yyyy') : '—' },
  { label: 'Lpn(s):', getValue: (item) => <LpnChips lpns={item.lpns ?? []} /> }
]

export const ResultsStep: React.FC<ResultsStepProps> = ({
  sharedState,
  setSharedState,
  goToStep
}) => {
  useEffect(() => {
    setSharedState((prev) => ({ ...prev, stepTitle: 'Múltiples ASN Encontradas' }))
    return () => {
      setSharedState((prev) => ({ ...prev, stepTitle: undefined }))
    }
  }, [])

  const handleSelect = (item: AsnReceptionResult) => {
    setSharedState((prev) => ({ ...prev, selectedResult: item }))
    goToStep(eStepAsnReception.ReceptionStep)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: 2, gap: 1, height: '100%', overflow: 'hidden' }}>
      <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2, flexShrink: 0 }}>
        Selecciona La ASN Correcta
      </Typography>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <ExpandableCardList
          items={sharedState.results}
          getKey={(item) => item.asnId}
          renderHeader={(item) => (
            <Typography variant='body2' fontWeight='bold'>{item.asnNumber}</Typography>
          )}
          fields={asnFields}
          onSelect={handleSelect}
        />
      </Box>
    </Box>
  )
}
